/**
 * R8 Feed Health primitives — visual atoms shared by Sources / Queue /
 * Coverage / Alert-exclusion tabs.
 *
 * Honesty rails inline:
 *   - StateBadge: icon + word + colour (CVD-safe)
 *   - HistoryStrip: 30-day per-day cells; same state palette; opacity
 *     differs for live (full) vs idle (faint) so "lots of idle days"
 *     reads as quiet, not as "all the same"
 *   - ReasonTag: BLOCKED's "permanence" attribute — always paired with
 *     the slate-violet --bm-blocked
 */

import { STATE_META, REASON_META, type FeedState } from './types';
import type { BlockedReason } from '@biomax/plugin-surveillance-feed';

export function StateBadge({ state, small = false }: { state: FeedState; small?: boolean }) {
  const m = STATE_META[state];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'var(--bm-font-mono)', fontSize: small ? 10 : 11,
      fontWeight: 700, letterSpacing: '.4px',
      color: m.cssVar, background: m.bgVar,
      border: `1px solid ${m.cssVar}55`, borderRadius: 5,
      padding: small ? '1px 6px' : '2px 8px', whiteSpace: 'nowrap',
    }}>
      <span aria-hidden>{m.icon}</span>{m.word}
    </span>
  );
}

export function ReasonTag({ reason }: { reason: BlockedReason }) {
  const m = REASON_META[reason];
  return (
    <span
      title={m.detail}
      style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10,
        color: 'var(--bm-blocked)',
        border: '1px solid var(--bm-blocked-ring)',
        borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
      }}
    >{m.word}</span>
  );
}

/** 30-day history strip — one cell per day, oldest left → newest right.
 *  Cells use the state palette. Idle days are faded so a long quiet
 *  source doesn't visually shout. */
export function HistoryStrip({ history, width = 132 }: { history: readonly FeedState[]; width?: number }) {
  if (history.length === 0) return null;
  const cell = width / history.length;
  return (
    <svg
      width={width} height={16}
      style={{ display: 'block', flexShrink: 0 }}
      role="img"
      aria-label={`30-day status history, ${history.length} days`}
    >
      {history.map((s, i) => {
        const m = STATE_META[s];
        const op = s === 'live' ? 0.85 : s === 'idle' ? 0.3 : 0.92;
        return (
          <rect
            key={i}
            x={i * cell} y={0}
            width={Math.max(1, cell - 0.6)} height={16}
            rx={1}
            fill={m.cssVar}
            opacity={op}
          />
        );
      })}
    </svg>
  );
}

/** Expanded 30-day history strip — CD R9 Q5 verdict ("keep 30 cells +
 *  expand-on-click"). Same data + same state palette as `HistoryStrip`,
 *  but bigger cells + hoverable per-day tooltip + bottom-axis labels at
 *  Day -30 / Day -15 / Today. Used in the SourcesView expanded-row panel. */
export function HistoryStripExpanded({
  history, width = 480, height = 48,
}: { history: readonly FeedState[]; width?: number; height?: number }) {
  if (history.length === 0) return null;
  const n = history.length;
  const cell = width / n;
  // Position labels at days -30 (leftmost), -15 (mid), and Today (rightmost)
  const labels: { i: number; t: string }[] = [
    { i: 0, t: `Day -${n - 1}` },
    { i: Math.floor((n - 1) / 2), t: `Day -${n - 1 - Math.floor((n - 1) / 2)}` },
    { i: n - 1, t: 'Today' },
  ];
  return (
    <svg
      width={width} height={height + 18}
      style={{ display: 'block', flexShrink: 0 }}
      role="img"
      aria-label={`Expanded ${n}-day status history`}
    >
      {history.map((s, i) => {
        const m = STATE_META[s];
        const op = s === 'live' ? 0.85 : s === 'idle' ? 0.3 : 0.92;
        const label = `${s.toUpperCase()} · ${n - 1 - i === 0 ? 'today' : `${n - 1 - i}d ago`}`;
        return (
          <rect
            key={i}
            x={i * cell} y={0}
            width={Math.max(1, cell - 1)} height={height}
            rx={2}
            fill={m.cssVar}
            opacity={op}
          >
            <title>{label}</title>
          </rect>
        );
      })}
      {labels.map(l => (
        <text
          key={l.i}
          x={l.i * cell + cell / 2}
          y={height + 14}
          textAnchor={l.i === 0 ? 'start' : l.i === n - 1 ? 'end' : 'middle'}
          fontFamily="var(--bm-font-mono)"
          fontSize={10}
          fill="var(--bm-text-subtle)"
        >{l.t}</text>
      ))}
    </svg>
  );
}

/** Inline button for per-source / per-queue actions (Retry / Pause / etc.). */
export function FhBtn({
  children, onClick, ghost = false, title,
}: { children: React.ReactNode; onClick?: () => void; ghost?: boolean; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: '3px 9px', borderRadius: 4, cursor: 'pointer',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
        border: ghost ? '1px solid var(--bm-border)' : 'none',
        background: ghost ? 'transparent' : 'var(--bm-accent, #6ea3ff)',
        color: ghost ? 'var(--bm-text-muted)' : 'var(--bm-bg)',
      }}
    >{children}</button>
  );
}

/** Small icon-only button for queue verbs (⏸ / ▶ / ↻ / ▲ / ⤓). */
export function QueueVerbBtn({ icon, label, onClick }: { icon: string; label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        display: 'inline-grid', placeItems: 'center',
        width: 24, height: 24, borderRadius: 4, cursor: 'pointer',
        fontSize: 12, lineHeight: 1,
        color: 'var(--bm-text-subtle)',
        border: '1px solid var(--bm-border)',
        background: 'transparent',
      }}
    >{icon}</button>
  );
}
